/** @format */

import * as Sentry from "@sentry/react";
import { QueryClient } from "@tanstack/react-query";

import { isExpectedRequestError } from "./api/galaxieClient";

/**
 * Create a new QueryClient instance
 * Used for both SSR and client-side rendering
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 1000 * 60 * 60 * 24, // 24 hours (for IndexedDB persistence)
        refetchOnWindowFocus: false,
        retry: 1,
      },
      mutations: {
        onError: (error: unknown) => {
          // Log mutation errors to Sentry in production, but skip expected /
          // transient outcomes (offline NetworkError/TimeoutError, or a 404 on a
          // deleted resource such as forking a stale RunView) — these are not
          // actionable bugs (JAVASCRIPT-REACT-F1 / FM / ES).
          if (
            !isExpectedRequestError(error) &&
            !import.meta.env.DEV &&
            import.meta.env.VITE_LOCAL !== "True"
          ) {
            Sentry.captureException(error, {
              tags: { errorType: "react_query_mutation_error" },
            });
          }
          // Log to console in development for debugging
          if (import.meta.env.DEV) {
            console.error("React Query mutation error:", error);
          }
        },
      },
    },
  });
}

/**
 * Singleton queryClient instance for use in mutations and other non-component contexts
 * For SSR, create a new queryClient per request instead
 */
export const queryClient = createQueryClient();
